package kz.dentalcrm.app.screens.services.detail

import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.error_message
import dentalcrm.composeapp.generated.resources.snackbar_service_deleted
import dentalcrm.composeapp.generated.resources.snackbar_service_updated
import kotlinx.collections.immutable.toPersistentList
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import kz.dentalcrm.app.core.base.BaseStateViewModel
import kz.dentalcrm.app.core.base.Selectable
import kz.dentalcrm.app.core.utils.Result
import kz.dentalcrm.app.data.network.request_model.AddServiceRequest
import kz.dentalcrm.app.data.network.response_model.services_category.ServiceCategory
import kz.dentalcrm.app.domain.ServicesRepository
import kz.dentalcrm.app.screens.services.detail.mvi.EditServiceSideEffect
import kz.dentalcrm.app.screens.services.detail.mvi.EditServiceState
import kz.dentalcrm.app.screens.services.detail.mvi.EditServiceViewEvent
import kz.dentalcrm.app.theme.SnackbarController
import kz.dentalcrm.app.theme.SnackbarEvent
import org.jetbrains.compose.resources.getString
import org.koin.core.component.inject

class EditServiceViewModel : BaseStateViewModel<
        EditServiceViewEvent, EditServiceState, EditServiceSideEffect>() {

    private val serviceRepository: ServicesRepository by inject()

    private val _isLoading = MutableStateFlow(false)
    val loading: StateFlow<Boolean> = _isLoading

    init {
        if (viewState.value.categories.isEmpty()) {
            getServiceCategories()
        }
    }

    private fun getServiceCategories() {
        setState {
            copy(
                screenState = EditServiceState.ScreenState.Loading
            )
        }
        launch(Dispatchers.Default) {
            when (val result = serviceRepository.getServiceCategories()) {
                is Result.Success -> {
                    result.value?.let { data ->
                        setState {
                            copy(
                                categories = data.map {
                                    Selectable(
                                        selected = false,
                                        data = it
                                    )
                                }.toPersistentList()
                            )
                        }
                    }
                }

                is Result.Error -> {

                }

                is Result.Failure -> {

                }
            }
            setState {
                copy(
                    screenState = EditServiceState.ScreenState.Idle
                )
            }
        }
    }

    private fun editService(
        id: Int,
        name: String,
        description: String,
        duration: Int,
        price: Int,
        category: ServiceCategory
    ) {
        setState {
            copy(
                screenState = EditServiceState.ScreenState.Adding
            )
        }
        co.touchlab.kermit.Logger.i("Edit service called:") {
            "Called"
        }
        launch(Dispatchers.Default) {
            when (val result = serviceRepository.editService(
                request = AddServiceRequest(
                    category_id = category.id.toString(),
                    description = description,
                    duration = duration,
                    max_price = price,
                    name = name,
                    price = price,
                ),
                id = id
            )) {
                is Result.Success -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.snackbar_service_updated), SnackbarEvent.SnackbarType.Green))
                    setState {
                        copy(
                            screenState = EditServiceState.ScreenState.Added
                        )
                    }
                }

                is Result.Error -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState {
                        copy(
                            screenState = EditServiceState.ScreenState.Error(
                                error = result.message
                                    ?: "Неизвестная ошибка (Проверьте введенные данные)"
                            )
                        )
                    }
                }

                else -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState {
                        copy(
                            screenState = EditServiceState.ScreenState.Error(
                                error = "Неизвестная ошибка (Проверьте введенные данные)"
                            )
                        )
                    }
                }
            }
        }
    }

    private fun deleteService(id: Int) {
        setState { copy(screenState = EditServiceState.ScreenState.Adding) }
        launch(Dispatchers.Default) {
            when (val result = serviceRepository.deleteService(id = id)) {
                is Result.Success -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.snackbar_service_deleted), SnackbarEvent.SnackbarType.Green))
                    setState { copy(screenState = EditServiceState.ScreenState.Deleted) }
                }
                is Result.Error -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState {
                        copy(
                            screenState = EditServiceState.ScreenState.Error(
                                error = result.message ?: "Неизвестная ошибка (Проверьте введенные данные)"
                            )
                        )
                    }
                }
                else -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState {
                        copy(
                            screenState = EditServiceState.ScreenState.Error(
                                error = "Неизвестная ошибка (Проверьте введенные данные)"
                            )
                        )
                    }
                }
            }
        }
    }

    override fun setInitialState() = EditServiceState()

    override fun handleEvents(event: EditServiceViewEvent) {
        when (event) {
            is EditServiceViewEvent.EditService -> {
                editService(
                    id = event.id,
                    name = event.naming,
                    description = event.description,
                    duration = event.duration.toInt(),
                    price = event.price.toInt(),
                    category = viewState.value.categories.first { it.selected }.data
                )
            }

            is EditServiceViewEvent.DeleteService -> {
                deleteService(id = event.id)
            }

            is EditServiceViewEvent.CategorySelected -> {
                val selections = viewState.value.categories.toMutableList().map {
                    Selectable(
                        selected = it.data == event.category,
                        data = it.data
                    )
                }
                setState {
                    copy(
                        categories = selections.toPersistentList()
                    )
                }
            }

            EditServiceViewEvent.Reset -> setState { copy(screenState = EditServiceState.ScreenState.Idle) }
        }
    }

    override fun onCleared() {
        setState { EditServiceState() }
        _isLoading.value = false
    }
}